<template>
  <div class="inquiry-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportTitle }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">Retry</b-button>
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img">
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName }}<span v-if="headerBranch" class="text-muted"> - {{ headerBranch }}</span>
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{ reportTitle }}</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>


      <!-- Campus-wise boxes when campusID is 1 -->
      <template v-if="isAllCampusesView">
        <div v-if="!campusGroups.length" class="empty-state">
          <feather-icon icon="InboxIcon" size="32" class="text-muted mb-2" />
          <p>No records found for the selected criteria</p>
        </div>

        <section
          v-for="(group, groupIndex) in campusGroups"
          :key="group.key"
          class="campus-block"
        >
          <div class="campus-heading">
            <div class="campus-heading__left">
              <span class="campus-heading__index">{{ groupIndex + 1 }}</span>
              <span class="campus-heading__name">{{ group.name }}</span>
            </div>
            <span class="campus-heading__count">{{ group.items.length }} records</span>
          </div>

          <div class="table-container">
            <table class="ledger-style-table">
              <thead>
                <tr>
                  <th class="text-center" style="width: 3%">#</th>
                  <th class="text-center" style="width: 8%">DATE</th>
                  <th class="text-left" style="width: 12%">NAME</th>
                  <th class="text-left" style="width: 12%">FATHER'S NAME</th>
                  <th class="text-center" style="width: 8%">CONTACT</th>
                  <th class="text-center" style="width: 6%">AGE</th>
                  <th class="text-center" style="width: 8%">CLASS</th>
                  <th class="text-left" style="width: 10%">LAST SCHOOL</th>
                  <th class="text-left" style="width: 10%">PURPOSE</th>
                  <th class="text-left" style="width: 12%">ADDRESS</th>
                  <th class="text-center" style="width: 11%">NEXT FOLLOW-UP</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in group.items" :key="group.key + '-' + index">
                  <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                  <td class="text-center">{{ formatDate(getInquiry(item).date) }}</td>
                  <td class="text-left font-weight-bold">
                    {{ (getInquiry(item).name || "-").toUpperCase() }}
                  </td>
                  <td class="text-left">
                    {{ (getInquiry(item).father_name || "-").toUpperCase() }}
                  </td>
                  <td class="text-center">{{ getInquiry(item).contact || "-" }}</td>
                  <td class="text-center">{{ getInquiry(item).age || "-" }}</td>
                  <td class="text-center">{{ getClassName(item) }}</td>
                  <td class="text-left">{{ getInquiry(item).lastSchool || "-" }}</td>
                  <td class="text-left">{{ getInquiry(item).purpose || "-" }}</td>
                  <td class="text-left address-cell">
                    {{ getInquiry(item).address || getInquiry(item).residentialArea || "-" }}
                  </td>
                  <td class="text-center">
                    {{ formatDate(getInquiry(item).nextFollowup) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>

      <!-- Single campus table -->
      <div v-else class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 3%">#</th>
              <th class="text-center" style="width: 8%">DATE</th>
              <th class="text-left" style="width: 12%">NAME</th>
              <th class="text-left" style="width: 12%">FATHER'S NAME</th>
              <th class="text-center" style="width: 8%">CONTACT</th>
              <th class="text-center" style="width: 6%">AGE</th>
              <th class="text-center" style="width: 8%">CLASS</th>
              <th class="text-left" style="width: 10%">LAST SCHOOL</th>
              <th class="text-left" style="width: 10%">PURPOSE</th>
              <th class="text-left" style="width: 12%">ADDRESS</th>
              <th class="text-center" style="width: 11%">NEXT FOLLOW-UP</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportItems" :key="index">
              <td class="text-center font-weight-bold">{{ index + 1 }}</td>
              <td class="text-center">{{ formatDate(getInquiry(item).date) }}</td>
              <td class="text-left font-weight-bold">
                {{ (getInquiry(item).name || "-").toUpperCase() }}
              </td>
              <td class="text-left">
                {{ (getInquiry(item).father_name || "-").toUpperCase() }}
              </td>
              <td class="text-center">{{ getInquiry(item).contact || "-" }}</td>
              <td class="text-center">{{ getInquiry(item).age || "-" }}</td>
              <td class="text-center">{{ getClassName(item) }}</td>
              <td class="text-left">{{ getInquiry(item).lastSchool || "-" }}</td>
              <td class="text-left">{{ getInquiry(item).purpose || "-" }}</td>
              <td class="text-left address-cell">
                {{ getInquiry(item).address || getInquiry(item).residentialArea || "-" }}
              </td>
              <td class="text-center">
                {{ formatDate(getInquiry(item).nextFollowup) }}
              </td>
            </tr>
            <tr v-if="reportItems.length === 0">
              <td colspan="11" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No records found for the selected criteria</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from 'bootstrap-vue'
import axios from 'axios'

export default {
  name: 'InquiryReportPrint',
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: 'MY SKOOL',
      campusBranch: '',
      schoolLogo: null,
      branches: [],
      db: '',
      cID: '',
      classID: 0,
      sessionID: 0,
      inquiryType: 'inquiry',
      dateFrom: null,
      dateTill: null,
    }
  },
  computed: {
    reportTitle() {
      return this.inquiryType === 'visitor' ? 'Visitors Report' : 'Student Inquiry Report'
    },
    isAllCampusesView() {
      return Number(this.cID) === 1
    },
    headerBranch() {
      return this.isAllCampusesView ? 'All Campuses' : this.campusBranch
    },
    currentDateTime() {
      const now = new Date()
      return now.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    campusGroups() {
      if (!this.isAllCampusesView || !this.reportItems.length) return []

      const map = {}
      const groups = []

      this.reportItems.forEach((item) => {
        const campusID = this.getCampusId(item)
        const name = this.resolveCampusName(item)
        const key = campusID != null && campusID !== '' ? `id-${campusID}` : `name-${name}`

        if (!map[key]) {
          map[key] = { key, campusID, name, items: [] }
          groups.push(map[key])
        }
        map[key].items.push(item)
      })

      return groups.sort((a, b) =>
        String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
      )
    },
  },
  created() {
    this.initReport()
  },
  methods: {
    initReport() {
      const query = this.$route.query
      this.db = query.db || this.$store.state.userData.db
      this.cID = query.cID || 0
      this.classID = query.classID ?? query.cls ?? 0
      this.sessionID =
        query.sessionID === null ||
        query.sessionID === undefined ||
        query.sessionID === ''
          ? 0
          : Number(query.sessionID)
      this.inquiryType = query.inquiryType || 'inquiry'
      this.dateFrom = this.normalizeInquiryReportDate(query.dateFrom)
      this.dateTill = this.normalizeInquiryReportDate(query.dateTill)

      if (!this.db) {
        this.error = 'Database identifier is missing.'
        return
      }

      if (
        this.sessionID === null ||
        this.sessionID === undefined ||
        Number.isNaN(Number(this.sessionID))
      ) {
        this.error = 'Session is required to load this report.'
        return
      }

      this.loadCampusInfo()
      this.loadReportData()
    },
    async loadCampusInfo() {
      try {
        const branches = this.$store.state.branches || []
        this.branches = branches
        const currentBranch = branches.find(b => b.id == this.cID) || this.$store.state.currentBranch
        if (currentBranch) {
          this.campusName = currentBranch.organizationName || currentBranch.branch || 'MY SKOOL'
          this.campusBranch = this.isAllCampusesView
            ? 'All Campuses'
            : (currentBranch.branch || '')
          this.schoolLogo = currentBranch.logo
        } else if (this.isAllCampusesView) {
          this.campusBranch = 'All Campuses'
        }
      } catch (e) {
        console.warn('Campus info fetch failed')
      }
    },
    async loadReportData() {
      this.loading = true
      this.error = null
      
      try {
        const payload = {
          db: this.db,
          campusID: Number(this.cID) || 0,
          inquiryType: this.inquiryType,
          classID: Number(this.classID) || 0,
          sessionID: Number(this.sessionID) || 0,
          dateFrom: this.normalizeInquiryReportDate(this.dateFrom),
          dateTill: this.normalizeInquiryReportDate(this.dateTill),
        }

        const url = `${this.$store.state.domain}StudentInquiries/GetInquiryReport`
        const response = await axios.post(url, payload, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            'Content-Type': 'application/json',
          },
        })
        
        if (response.data && response.data.status === 'success') {
          this.reportItems = response.data.data || []
        } else if (Array.isArray(response.data)) {
          this.reportItems = response.data
        } else if (response.data && Array.isArray(response.data.data)) {
          this.reportItems = response.data.data
        } else {
          this.error = response.data?.message || 'Failed to load report data.'
        }
      } catch (err) {
        console.error('Error fetching inquiry report:', err)
        this.error =
          err?.response?.data?.message ||
          'Server error while fetching report.'
      } finally {
        this.loading = false
      }
    },
    normalizeInquiryReportDate(value) {
      if (value === null || value === undefined || value === '') return null
      return value
    },
    getInquiry(item) {
      return (item && item.inquiry) || item || {}
    },
    getCampusId(item) {
      const inquiry = this.getInquiry(item)
      const id = item && item.campusID != null ? item.campusID : inquiry.campusID
      return id == null || id === '' ? null : id
    },
    getCampusNameById(id) {
      if (id == null || id === '') return ''
      const match = (this.branches || []).find(b => b.id == id)
      return match ? (match.branch || match.name || '') : ''
    },
    resolveCampusName(item) {
      const inquiry = this.getInquiry(item)
      return (
        (item && item.campusBranch) ||
        inquiry.campusBranch ||
        (item && item.campusName) ||
        inquiry.campusName ||
        (item && item.branch) ||
        inquiry.branch ||
        this.getCampusNameById(this.getCampusId(item)) ||
        'Unknown Campus'
      )
    },
    getClassName(item) {
      const inquiry = this.getInquiry(item)
      return (item && item.className) || inquiry.className || '-'
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "-"
      const date = new Date(dateStr)
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    print() {
      window.print()
    },
    close() {
      window.close()
    }
  }
}
</script>

<style lang="scss" scoped>
.inquiry-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: 'Outfit', 'Inter', sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255,255,255,0.3);
  
  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span { margin-left: 4px; }
  &:active { transform: scale(0.96); }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(108, 92, 231, 0.3); }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
  &:hover { color: #e74c3c; border-color: #fab1a0; }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
}

.report-header-modern {
  border-bottom: 3px solid #333;
  padding-bottom: 20px;
  
  .school-logo-img {
    height: 100px;
    width: auto;
  }
  
  .school-name-full {
    font-size: 1.8rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
  }
  
  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.summary-section {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.summary-card {
  min-width: 160px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 10px;
  padding: 0.85rem 1.1rem;
  display: flex;
  flex-direction: column;

  .label {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: #636e72;
  }

  .value {
    font-size: 1.35rem;
    font-weight: 800;
    color: #2c3e50;
    line-height: 1.3;
  }
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #636e72;
}

.campus-block {
  margin-bottom: 1.75rem;
  page-break-inside: auto;
}

.campus-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  margin-bottom: 0;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  page-break-after: avoid;
  page-break-inside: avoid;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  &__index {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #eef2ff;
    color: #4338ca;
    font-size: 0.8rem;
    font-weight: 800;
    flex-shrink: 0;
  }

  &__name {
    font-size: 1rem;
    font-weight: 800;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  &__count {
    font-size: 0.82rem;
    font-weight: 700;
    color: #fff;
    background: #2c3e50;
    padding: 0.3rem 0.75rem;
    border-radius: 999px;
    white-space: nowrap;
  }
}

.campus-block .ledger-style-table {
  margin-top: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;

  th, td {
    border: 1px solid #dee2e6;
    padding: 12px 10px;
    font-size: 0.9rem;
  }

  thead {
    background: #2c3e50;
    th {
      color: white;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.85rem;
    }
  }

  tbody tr:nth-child(even) { background-color: #f8f9fa; }
  .address-cell { font-size: 0.8rem; color: #666; }
}

@media print {
  .no-print { display: none !important; }
  .inquiry-report { background: white; padding: 0; }
  .report-page { box-shadow: none; border: none; padding: 0.5cm; width: 100%; max-width: 100%; margin: 0; }
  .summary-section { margin-bottom: 12px !important; }
  .summary-card {
    background: #f8f9fa !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .campus-heading {
    background: #f8f9fa !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .campus-heading__count {
    background: #2c3e50 !important;
    color: #fff !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .campus-block + .campus-block {
    page-break-before: auto;
  }
  .ledger-style-table { 
    th, td { padding: 6px 8px; border: 1px solid #000 !important; color: #000; }
    thead th { background: #eee !important; color: #000 !important; -webkit-print-color-adjust: exact; }
  }
}
</style>
